import { Icon } from '~/components/ui';
import { useT } from '~/i18n';
import { H2_EMPLOYER_MAX, H2_STATUSES, asksForEmployer, type H2Status } from '../lib/immigration';

/**
 * Las cuatro opciones de «Tu situación migratoria» como tarjetas que se eligen
 * con un toque, y el campo opcional del empleador para quien ya tuvo (o tiene)
 * una H-2. Controlado: lo usan el paso del alta y la tarjeta de Preferencias,
 * y cada uno guarda a su manera.
 *
 * Tarjetas y no un `<select>`: el nombre corto solo («Returner», «Extensión»)
 * no le dice nada a quien nunca oyó la palabra; la frase de abajo sí. Mismo
 * dibujo que las visas del cuestionario (ScreeningWizard).
 */
interface Props {
  status: H2Status | null;
  employer: string;
  onStatus: (status: H2Status) => void;
  onEmployer: (employer: string) => void;
  disabled?: boolean;
  /** Para el `id` del campo: el alta y Preferencias no pueden repetirlo. */
  idPrefix: string;
}

export default function H2StatusPicker({ status, employer, onStatus, onEmployer, disabled, idPrefix }: Props) {
  const t = useT('onboarding').immigration;
  const employerId = `${idPrefix}-h2-employer`;

  return (
    <div className="space-y-3">
      <div role="radiogroup" aria-label={t.groupLabel} className="space-y-2">
        {H2_STATUSES.map((option) => {
          const selected = status === option;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onStatus(option)}
              className="w-full text-left p-3 flex items-start gap-3"
              style={{
                borderRadius: 'var(--r-inner)',
                border: `1px solid ${selected ? 'var(--hairline-hi)' : 'var(--hairline)'}`,
                background: selected ? 'var(--surface-hi)' : 'transparent',
              }}
            >
              <span
                className="grid place-items-center shrink-0 mt-0.5"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 999,
                  border: `1px solid ${selected ? 'transparent' : 'var(--hairline)'}`,
                  background: selected ? 'var(--purple-cta)' : 'transparent',
                  color: '#fff',
                }}
              >
                {selected && <Icon name="check" size={13} />}
              </span>
              <span className="min-w-0">
                <span className="block font-medium text-sm">{t.options[option].label}</span>
                <span className="block text-xs hush">{t.options[option].hint}</span>
              </span>
            </button>
          );
        })}
      </div>

      {asksForEmployer(status) && (
        <div className="form-control w-full text-left">
          <label className="label pb-1" htmlFor={employerId}>
            <span className="label-text font-medium">
              {t.employerLabel}
              <span className="hush font-normal">{t.employerOptional}</span>
            </span>
          </label>
          <input
            id={employerId}
            type="text"
            className="input w-full"
            maxLength={H2_EMPLOYER_MAX}
            placeholder={t.employerPlaceholder}
            value={employer}
            disabled={disabled}
            onChange={(e) => onEmployer(e.target.value)}
          />
          <p className="text-xs hush mt-1">{t.employerHint}</p>
        </div>
      )}
    </div>
  );
}
